import { useEffect, useState } from "react";
import { usePatientContext } from "../classes/contexts/PatientContext";
import {
  mapOrientationOptionsDxToMorphology,
  MorphologyOrientation,
} from "../classes/enums/MedicalImagesEnum";
import { getPatientMorphologyPicture } from "../services/ImageService";
import useTranslation from "./i18n/useTranslation";
import { useRouteState } from "./useRouteState";

export function usePatientMorphologyImages() {
  const { patientImages } = usePatientContext();
  const { params } = useRouteState();
  const { t } = useTranslation();
  const [imagesWithUrls, setImagesWithUrls] = useState([]);
  const [imagesWithUrlsNoBg, setImagesWithUrlsNoBg] = useState([]);

  const parseImageTitle = title => {
    const defaults = {
      modality: t("346"),
      viewType: t("346"),
      acquisitionDate: null,
      uploadDate: null,
      orientation: MorphologyOrientation.LEFT,
      isNoBg: false,
      position: MorphologyOrientation.LEFT,
    };

    try {
      const parts = title.split("_");
      if (parts.length < 5) return defaults;

      const isNoBg = title.includes("_no_bg");
      const viewType = parts[2];
      const viewTypeNormalized = viewType.replace(/\s+/g, "");
      let orientation = defaults.orientation;
      if (mapOrientationOptionsDxToMorphology[viewTypeNormalized]) {
        orientation = mapOrientationOptionsDxToMorphology[viewTypeNormalized];
      } else {
        if (
          viewTypeNormalized.includes("Sagittal") &&
          viewTypeNormalized.includes("AP")
        ) {
          orientation = mapOrientationOptionsDxToMorphology.SagittalAP;
        } else if (
          viewTypeNormalized.includes("Sagittal") &&
          viewTypeNormalized.includes("PA")
        ) {
          orientation = mapOrientationOptionsDxToMorphology.SagittalPA;
        } else if (
          viewTypeNormalized.includes("Coronal") &&
          viewTypeNormalized.includes("LR")
        ) {
          orientation = mapOrientationOptionsDxToMorphology.CoronalLR;
        } else if (
          viewTypeNormalized.includes("Coronal") &&
          viewTypeNormalized.includes("RL")
        ) {
          orientation = mapOrientationOptionsDxToMorphology.CoronalRL;
        }
      }
      const modality = parts[1];
      let acquisitionDate = parts[3] || null;
      let uploadDate = null;
      if (isNoBg) {
        // _no_bg splits into ["no", "bg.ext"] — find the "no" part that is followed by "bg*"
        const nobgIndex = parts.findIndex(
          (part, i) => part === "no" && parts[i + 1]?.startsWith("bg")
        );
        uploadDate =
          nobgIndex > 0
            ? parts[nobgIndex - 1]
            : parts[parts.length - 2] || null;
      } else {
        const lastPart = parts[parts.length - 1];
        uploadDate = lastPart.includes(".") ? lastPart.split(".")[0] : lastPart;
      }
      return {
        modality,
        viewType,
        acquisitionDate,
        uploadDate,
        orientation,
        position: orientation,
        isNoBg,
      };
    } catch {
      return defaults;
    }
  };

  // Helper function to sort images by date (newest first)
  const sortImagesByDate = images => {
    return images.sort((a, b) => {
      // Handle YYYYMMDD string format or fallback to 0
      const dateStrA = a.date || "00000000";
      const dateStrB = b.date || "00000000";

      // Convert YYYYMMDD to comparable format or use string comparison directly
      // Since YYYYMMDD format naturally sorts correctly as strings
      return dateStrB.localeCompare(dateStrA); // Descending order (newest first)
    });
  };

  useEffect(() => {
    const urlsToRevoke = [];
    if (!patientImages?.length) {
      setImagesWithUrls([]);
      setImagesWithUrlsNoBg([]);
      return;
    }

    let active = true;

    // Group images by base key, keeping arrays for multiples
    const imagesByBaseKey = new Map();
    patientImages.forEach(img => {
      const parsedTitle = img.title || t("346");
      const titleInfo = parseImageTitle(parsedTitle);
      const baseKey = `${titleInfo.modality}_${titleInfo.viewType}_${titleInfo.orientation}`;
      if (!imagesByBaseKey.has(baseKey)) {
        imagesByBaseKey.set(baseKey, { standard: [], noBg: [] });
      }
      const entry = imagesByBaseKey.get(baseKey);
      const infoWithMeta = {
        ...titleInfo,
        title: parsedTitle,
        date: titleInfo.acquisitionDate || titleInfo.uploadDate || null,
      };
      if (titleInfo.isNoBg) {
        entry.noBg.push(infoWithMeta);
      } else {
        entry.standard.push(infoWithMeta);
      }
    });

    // Sort each group by date
    for (const entry of imagesByBaseKey.values()) {
      entry.standard = sortImagesByDate(entry.standard);
      entry.noBg = sortImagesByDate(entry.noBg);
    }

    (async () => {
      try {
        const standardImages = [];
        const noBgImages = [];

        for (const { standard, noBg } of imagesByBaseKey.values()) {
          if (!standard.length) continue; // must have at least one standard

          for (let i = 0; i < standard.length; i++) {
            const stdInfo = standard[i];
            // fetch standard
            const stdResp = await getPatientMorphologyPicture(
              params.patientId,
              stdInfo.title
            );
            const stdUrl = URL.createObjectURL(stdResp.data);
            urlsToRevoke.push(stdUrl);
            standardImages.push({ ...stdInfo, url: stdUrl });

            // fetch noBg counterpart or fallback
            const nobgInfo = noBg[i] || stdInfo;
            if (nobgInfo === stdInfo) {
              // fallback to standard URL
              noBgImages.push({ ...stdInfo, url: stdUrl });
            } else {
              const nobgResp = await getPatientMorphologyPicture(
                params.patientId,
                nobgInfo.title
              );
              const nobgUrl = URL.createObjectURL(nobgResp.data);
              urlsToRevoke.push(nobgUrl);
              noBgImages.push({ ...nobgInfo, url: nobgUrl });
            }
          }
        }

        if (active) {
          // Final sort of all images by date
          setImagesWithUrls(sortImagesByDate(standardImages));
          setImagesWithUrlsNoBg(sortImagesByDate(noBgImages));
        }
      } catch {
        // individual image fetch failures are handled above; nothing to surface
      }
    })();

    return () => {
      active = false;
      urlsToRevoke.forEach(url => URL.revokeObjectURL(url));
    };
  }, [params.patientId, patientImages]);

  return { imagesWithUrls, imagesWithUrlsNoBg };
}
